import { useMemo } from 'react';

import {
  hasAllPermissions,
  hasAnyPermission,
  hasPermission,
  type Permission,
} from '@bookora/shared';

import { useSessionStore } from '@/auth/token-store';

/**
 * ══════════════════════════════════════════════════════════════════════════
 *  THIS IS AN AFFORDANCE LAYER. IT IS NOT SECURITY.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `useCan()` decides whether to DRAW a button. Whether the button's action is
 * ALLOWED is decided by `PermissionsGuard` on the server, which re-resolves the
 * caller's role and per-staff overrides from Postgres on every request and
 * answers 403 — with a Persian message this panel then displays — no matter
 * what this hook returned.
 *
 * Three consequences worth internalising before writing a screen:
 *
 *   1. **Hiding is not protecting.** Anything reachable by typing a URL, or by
 *      an old tab whose cached permission list is stale, must still be safe.
 *      It is, because the data behind it comes from an endpoint that checks.
 *   2. **Never gate a MUTATION on `useCan` alone and then skip the error
 *      path.** The 403 can still arrive — the owner may have revoked the key
 *      between page load and click.
 *   3. **Never invent a permission key here.** The list comes from
 *      `@bookora/shared`, which the backend imports too. A key that exists only
 *      in the panel is a button that can never be granted.
 *
 * The list itself comes from `/auth/me` (the token's *effective* permissions:
 * role preset + per-staff grants − per-staff revocations) and is refreshed on
 * boot, on window focus, and after every clinic switch.
 */

/** Subscribed permission array for the active clinic. */
export function usePermissions(): Permission[] {
  return useSessionStore((state) => state.permissions);
}

/** `true` when the panel may render the affordance guarded by `permission`. */
export function useCan(permission: Permission | null | undefined): boolean {
  const permissions = usePermissions();
  return useMemo(
    () => (permission == null ? true : hasPermission(permissions, permission)),
    [permissions, permission],
  );
}

/** `true` when the caller holds at least one of `required`. */
export function useCanAny(required: readonly Permission[]): boolean {
  const permissions = usePermissions();
  return useMemo(
    () => (required.length === 0 ? true : hasAnyPermission(permissions, required)),
    [permissions, required],
  );
}

/** `true` when the caller holds every one of `required`. */
export function useCanAll(required: readonly Permission[]): boolean {
  const permissions = usePermissions();
  return useMemo(
    () => (required.length === 0 ? true : hasAllPermissions(permissions, required)),
    [permissions, required],
  );
}
